import { useContext, useState } from "react";
import { useSelector } from "react-redux";
import { AppContext } from "../../Context";
import { StoreState } from "../../store/rootReducer";
import { GetCodedValueAndAlias, IsFeatureInMapExtent } from "../../utils/arcgisUtils";
import Loader from "../genericComponents/Loader/Loader";
import ResultsFilter from "./ResultsFilter";
import SearchItem from "./SearchItem";
import "./SearchResults.css";
import type LayerSearchSource from "@arcgis/core/widgets/Search/LayerSearchSource";
import type { ResourceHandle } from "@arcgis/core/core/Handles";

interface SearchResultsContentProps {
    storeHighlightHandle: (handle: ResourceHandle | null) => void;
    clearHighlightedFeatures: () => void;
}

const SearchResultsContent = (props: SearchResultsContentProps) => {
    const { storeHighlightHandle, clearHighlightedFeatures } = props;

    const [selectedSource, setSelectedSource] = useState<string | null>(null);
    const [selectedStatus, setSelectedStatus] = useState<string | null>(null);
    const [filterByExtent, setFilterByExtent] = useState<boolean>(false);

    const appContext = useContext(AppContext);
    const searchResults = appContext?.searchResults.value ?? [];

    const searchResultsLoading = useSelector((state: StoreState) => state.search.searchResultsLoading);

    return (
        <>
            <div className="search-filter">
                <ResultsFilter
                    results={searchResults
                        .flatMap((result) => result.results?.map((r) => r.feature))
                        .filter((feature) => feature !== undefined)}
                    selectedSource={selectedSource}
                    setSelectedSource={setSelectedSource}
                    selectedStatus={selectedStatus}
                    setSelectedStatus={setSelectedStatus}
                />
                <label className="search-filter-label">
                    <input
                        type="checkbox"
                        checked={filterByExtent}
                        onChange={() => setFilterByExtent(!filterByExtent)}
                    />{" "}
                    Innenfor kartutsnitt
                </label>
            </div>
            <div className="search-content">
                {searchResults.map((results) => {
                    // Search only has layer sources (includeDefaultSources is off)
                    const source = results.source as LayerSearchSource;
                    const title = source.layer.title;
                    if (selectedSource !== null && !(title && selectedSource.includes(title))) {
                        return null;
                    }
                    return results.results?.map((result: any) => {
                        if (
                            GetCodedValueAndAlias(
                                source.layer,
                                "nfs_status",
                                result.feature.attributes["nfs_status"]
                            )[1] !== selectedStatus &&
                            selectedStatus !== null
                        ) {
                            return null;
                        }

                        if (filterByExtent) {
                            if (!IsFeatureInMapExtent(result.feature)) {
                                return null;
                            }
                        }

                        return (
                            <SearchItem
                                key={result.name + result.key + result.sourceIndex}
                                feature={result.feature}
                                storeHighlightHandle={storeHighlightHandle}
                                clearHighlightedFeatures={clearHighlightedFeatures}
                            />
                        );
                    });
                })}
                <span className="search-content-empty">Ingen resultater funnet.</span>
            </div>
            {searchResultsLoading && (
                <div className="search-loader">
                    <Loader inline size={20} />
                </div>
            )}
        </>
    );
};

export default SearchResultsContent;
